import React, { useCallback } from 'react';
import { Row, Col, Typography, Card } from 'antd';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { DASHBOARD_TILE_CONFIG, DASHBOARD_TILE_TYPES } from './utils';
import { useEntityRegistry } from '../useEntityRegistry';
import { useTranslation } from '../../i18n/useTranslation';
import { formatNumber } from '../shared/formatNumber';
import analytics, { EventType } from '../analytics';
import { PageRoutes } from '../../conf/Global';
import { navigateToSearchUrl } from '../search/utils/navigateToSearchUrl';

import { useGetEntityCountsQuery } from '../../graphql/app.generated';
import { EntityType } from '../../types.generated';
import { DOMAINS_FILTER_NAME, OWNERS_FILTER_NAME } from '../search/utils/constants';
import { getFieldValues, useDashboardFilters } from './dashboardFilters';

const Wrapper = styled(Card)`
    position: relative;

    .f-btn-action {
        position: absolute;
        top: 8px;
        z-index: 1;
        right: 8px;
    }
`;

export const EntityTilesSection = () => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const history = useHistory();
    const dashboardFilters = useDashboardFilters();

    const selectedOwners = getFieldValues(dashboardFilters, OWNERS_FILTER_NAME);
    const selectedDomains = getFieldValues(dashboardFilters, DOMAINS_FILTER_NAME);

    const { data: entityCountData, previousData: previousEntityCountData } = useGetEntityCountsQuery({
        variables: {
            input: {
                types: DASHBOARD_TILE_TYPES,
                ...(selectedOwners.length > 0 && { owners: selectedOwners }),
                ...(selectedDomains.length > 0 && { domains: selectedDomains }),
            },
        },
        fetchPolicy: 'no-cache',
    });

    // Keep previous counts visible while a filter-driven refetch is in flight.
    const effectiveCountData = entityCountData ?? previousEntityCountData;

    const getTileUrl = useCallback(
        (entityType: EntityType): string => {
            if (entityType === EntityType.GlossaryTerm) return PageRoutes.GLOSSARY;
            if (entityType === EntityType.DataProduct) return PageRoutes.BROWSE_DATAPRODUCT;
            if (entityType === EntityType.Domain) return PageRoutes.DOMAINS;
            return `${PageRoutes.BROWSE}/${entityRegistry.getPathName(entityType)}`;
        },
        [entityRegistry],
    );

    const onTileClick = useCallback(
        (entityType: EntityType) => {
            analytics.event({ type: EventType.HomePageBrowseResultClickEvent, entityType });
            if (entityType === EntityType.Tag) {
                navigateToSearchUrl({ type: EntityType.Tag, query: '*', history });
            } else {
                history.push(getTileUrl(entityType));
            }
        },
        [history, getTileUrl],
    );

    const getEntityCount = useCallback(
        (entityType: EntityType): string => {
            const countEntry = effectiveCountData?.getEntityCounts?.counts?.find(
                (entry) => entry.entityType === entityType,
            );
            return countEntry ? formatNumber(countEntry.count) : '0';
        },
        [effectiveCountData],
    );

    return (
        <Wrapper className="f-bordered f-content-padding-x py-1 mb-3">
            <Row>
                {DASHBOARD_TILE_CONFIG.map((tile) => (
                    <Col key={tile.entityType} lg={4} md={8} sm={12} xs={24}>
                        <Card
                            className="f-content-padding-y px-0"
                            style={{ cursor: 'pointer' }}
                            onClick={() => onTileClick(tile.entityType)}
                        >
                            <div className={`f-left-border-highlight ${tile.colorClass} py-0`}>
                                <Typography.Text className="f-text-big-content f-text-semibold f-color-dark-black-s100">
                                    {t(tile.labelKey)}
                                </Typography.Text>
                                <Typography.Title
                                    level={2}
                                    className="f-text-heading f-text-single-line f-text-semibold ma-0"
                                >
                                    {getEntityCount(tile.entityType)}
                                </Typography.Title>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>
        </Wrapper>
    );
};
